import * as React from 'react'
import {
    Box,
    Stack,
    Switch,
    Typography
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

const cloneValue = value => {
    if (typeof structuredClone === 'function')
        return structuredClone(value)

    return JSON.parse(JSON.stringify(value ?? {}))
}

export const FUNCTION_SELECTION_ITEMS = Object.freeze([
    { id: 'alliance-auto-help', label: 'Alliance Auto Help', pluginKeys: ['helprequests'] },
    { id: 'produce-components', label: 'ProduceComponents', pluginKeys: ['auto_craft_components', 'auto_craft_refinery'] },
    { id: 'tower-bot', label: 'TowerBot', pluginKeys: ['attackbarronsempire', 'attackbarronsglacier', 'attackbarronssands', 'attackbarronsfire'], normalOnly: true },
    { id: 'bth-tower-bot', label: 'BTH TowerBot', pluginKeys: ['attackbarronsempire', 'attackbarronsglacier', 'attackbarronssands', 'attackbarronsfire'], bthOnly: true },
    { id: 'berri-world', label: 'BerriWorld', pluginKeys: ['berikingdom'] },
    { id: 'automatic-feast', label: 'AutomaticFeast', pluginKeys: ['feast'] },
    { id: 'hospital-healer', label: 'Hospital Healer', pluginKeys: ['hospital'] },
    { id: 'nomads-bot', label: 'NomadsBot', pluginKeys: ['attacknomads'] },
    { id: 'castle-defense', label: 'CastleDefense', pluginKeys: ['flee'] },
    { id: 'foreign-castles-bot', label: 'ForeignCastlesBot', pluginKeys: ['attackaliens'] },
    { id: 'native-villages', label: 'Native Villages', pluginKeys: ['nativevillage'] },
    { id: 'alert-bot', label: 'AlertBot', pluginKeys: ['alert', 'twilio_alert', 'twilio_alert_alliance'] },
    { id: 'technicus', label: 'Technicus', pluginKeys: ['technicus'] },
    { id: 'armorer', label: 'Armorer', pluginKeys: ['armorer'] },
    { id: 'master-blacksmith', label: 'Master Blacksmith', pluginKeys: ['grabtools'], normalOnly: true },
    { id: 'bth-master-blacksmith', label: 'BTH Master Blacksmith', pluginKeys: ['grabtools'], bthOnly: true },
    { id: 'storm-bot', label: 'StormBot', pluginKeys: ['attackforts'] },
    { id: 'samurai-bot', label: 'SamuraiBot', pluginKeys: ['attacksams'] },
    { id: 'speed-settings', label: 'Speed Settings', pluginKeys: ['autospeed'] },
    { id: 'berri-green', label: 'BerriGreen', pluginKeys: ['attackberi'] },
    { id: 'recruit-bot', label: 'RecruitBot', pluginKeys: ['barracks_modified'], normalOnly: true },
    { id: 'bth-recruit-bot', label: 'BTH RecruitBot', pluginKeys: ['barracks_modified'], bthOnly: true },
    { id: 'bth-influence-attacks', label: 'BTH Influence Attacks', pluginKeys: ['bth_influence_attack'], bthOnly: true },
    { id: 'bth-influence-hunter', label: 'BTH Influence Hunter', pluginKeys: ['bth_influence_hunter'], bthOnly: true },
    { id: 'bth-map-attack', label: 'BTH Map Attack', pluginKeys: ['bth_map_attack'], bthOnly: true },
    { id: 'bth-tax-collector', label: 'BTH Automatic Tax Collector', pluginKeys: ['bth_tax_collector'], bthOnly: true },
    { id: 'tax-collector', label: 'Automatic Tax Collector', pluginKeys: ['tax_collector'], normalOnly: true },
    { id: 'food-check', label: 'Food Check', pluginKeys: ['meadrep'] },
    { id: 'tool-build', label: 'ToolBuild', pluginKeys: ['auto_recruit_tools'] },
    { id: 'equipment-seller', label: 'Equipment Seller', pluginKeys: ['equipment_seller'], normalOnly: true },
    { id: 'bth-equipment-seller', label: 'BTH Equipment Seller', pluginKeys: ['equipment_seller'], bthOnly: true },
    { id: 'kahn-bot', label: 'KhanBot', pluginKeys: ['attackkhan'] },
    { id: 'fortress-bot', label: 'FortressBot', pluginKeys: ['attackfortresses', 'attackfortresses_glacier', 'attackfortresses_sand'] },
    { id: 'skips-trade', label: 'SkipsTrade', pluginKeys: ['skips_trade'] },
    { id: 'message-management', label: 'MessageManagement', pluginKeys: ['chat', 'outgoing', 'Ressend'] }
])

export const buildAlphabeticalFunctionColumns = items => {
    const sortedItems = [...items].sort((left, right) => left.label.localeCompare(right.label, 'en', { sensitivity: 'base' }))
    const firstColumnLength = Math.ceil(sortedItems.length / 2)
    return [sortedItems.slice(0, firstColumnLength), sortedItems.slice(firstColumnLength)]
}

export const getVisibleFunctionSelectionItems = (items, bthMode = false) =>
    items.filter(item => bthMode ? !item.normalOnly : !item.bthOnly)

const FUNCTION_MODULE_TARGETS = Object.freeze({
    'produce-components': 'module:produce-components',
    'tower-bot': 'module:tower-bot',
    'bth-tower-bot': 'module:tower-bot',
    'recruit-bot': 'module:barracks_modified',
    'bth-recruit-bot': 'module:barracks_modified',
    'master-blacksmith': 'module:grabtools',
    'bth-master-blacksmith': 'module:grabtools',
    'equipment-seller': 'module:equipment_seller',
    'bth-equipment-seller': 'module:equipment_seller',
    'bth-influence-attacks': 'module:bth_influence_attack',
    'bth-influence-hunter': 'module:bth_influence_hunter',
    'fortress-bot': 'module:fortress-bot',
    'nomads-bot': 'module:camp-bot-nomads',
    'samurai-bot': 'module:camp-bot-samurai',
    'speed-settings': 'speed-settings',
    'alert-bot': 'group:call'
})

const CAMP_BOT_SETTINGS_KEY = 'campBotSettings'
const STORM_CARGO_PLUGIN_KEY = 'islandcargo'
const STORM_CARGO_SETTINGS_KEY = 'stormCargoSettings'
const TOWER_BOT_SETTINGS_KEY = 'towerBotSettings'
const FORTRESS_BOT_SETTINGS_KEY = 'fortressBotSettings'
const CAMP_BOT_TARGET_TYPES = Object.freeze({
    nomad: 'nomad',
    samurai: 'samurai'
})

const hasGroupedPluginLegacyConfig = (pluginState, settingsKey) => {
    if (!pluginState || typeof pluginState !== 'object')
        return false

    return Object.entries(pluginState).some(([key, value]) => {
        if (key === settingsKey || key === 'state' || key === 'forced')
            return false

        return value !== undefined
    })
}

export const resolveGroupedModuleRuntimeStates = (userPlugins, pluginKeys, settingsKey) => {
    const runtimeStates = Object.fromEntries(
        pluginKeys.map(pluginKey => {
            const pluginState = userPlugins?.[pluginKey]
            const savedSettings =
                pluginState?.[settingsKey] && typeof pluginState[settingsKey] === 'object' ?
                    pluginState[settingsKey] :
                    null

            if (savedSettings && Object.prototype.hasOwnProperty.call(savedSettings, 'enabled'))
                return [pluginKey, Boolean(savedSettings.enabled)]

            if (Boolean(pluginState?.state))
                return [pluginKey, true]

            if (hasGroupedPluginLegacyConfig(pluginState, settingsKey))
                return [pluginKey, true]

            return [pluginKey, false]
        })
    )

    if (!Object.values(runtimeStates).some(Boolean) && pluginKeys.length > 0)
        runtimeStates[pluginKeys[0]] = true

    return runtimeStates
}

const isGroupedModuleActive = (userPlugins, pluginKeys) =>
    pluginKeys.some(pluginKey => Boolean(userPlugins?.[pluginKey]?.state))

const resolveFunctionTarget = item =>
    FUNCTION_MODULE_TARGETS[item.id] ??
    (item.pluginKeys.length > 0 ? `module:${item.pluginKeys[0]}` : '')

export const isCampBotEnabled = userPlugins =>
    Boolean(userPlugins?.attack?.state) ||
    Boolean(userPlugins?.attack?.[CAMP_BOT_SETTINGS_KEY]?.enabled) ||
    Boolean(userPlugins?.attacknomads?.state) ||
    Boolean(userPlugins?.attacksams?.state)

export const resolveCampBotTargetType = userPlugins => {
    const nomadEnabled = Boolean(userPlugins?.attacknomads?.state)
    const samuraiEnabled = Boolean(userPlugins?.attacksams?.state)
    const activeTargetTypes = [
        [CAMP_BOT_TARGET_TYPES.nomad, nomadEnabled],
        [CAMP_BOT_TARGET_TYPES.samurai, samuraiEnabled]
    ].filter(([, enabled]) => enabled)

    if (activeTargetTypes.length === 1)
        return activeTargetTypes[0][0]

    const configuredTargetType = String(userPlugins?.attack?.[CAMP_BOT_SETTINGS_KEY]?.targetType ?? '')
        .trim()
        .toLowerCase()

    if (configuredTargetType === CAMP_BOT_TARGET_TYPES.samurai)
        return CAMP_BOT_TARGET_TYPES.samurai

    if (configuredTargetType === CAMP_BOT_TARGET_TYPES.nomad)
        return CAMP_BOT_TARGET_TYPES.nomad

    if (samuraiEnabled)
        return CAMP_BOT_TARGET_TYPES.samurai

    return CAMP_BOT_TARGET_TYPES.nomad
}

export const isFunctionEnabled = (userPlugins, item, pluginKeys) => {
    if (item.id === 'bth-influence-hunter')
        return userPlugins?.bth_influence_hunter?.enabled === true && userPlugins?.bth_influence_hunter?.state !== false

    if (item.id === 'nomads-bot')
        return Boolean(userPlugins?.attacknomads?.state)

    if (item.id === 'samurai-bot')
        return Boolean(userPlugins?.attacksams?.state)

    if (item.id === 'tower-bot' || item.id === 'bth-tower-bot' || item.id === 'fortress-bot')
        return isGroupedModuleActive(userPlugins, pluginKeys)

    return pluginKeys.some(pluginKey => Boolean(userPlugins?.[pluginKey]?.state))
}

export const buildCampBotFunctionToggleState = (userPlugins, nextState) => {
    const nextPlugins = cloneValue(userPlugins ?? {})
    const enabled = Boolean(nextState)
    const targetType = resolveCampBotTargetType(nextPlugins)

    nextPlugins.attack ??= {}
    nextPlugins.attacknomads ??= {}
    nextPlugins.attacksams ??= {}

    nextPlugins.attack.state = enabled

    const campBotSettings = nextPlugins.attack[CAMP_BOT_SETTINGS_KEY]
    nextPlugins.attack[CAMP_BOT_SETTINGS_KEY] =
        campBotSettings && typeof campBotSettings === 'object' && !Array.isArray(campBotSettings) ?
            campBotSettings :
            {}
    nextPlugins.attack[CAMP_BOT_SETTINGS_KEY].enabled = enabled
    nextPlugins.attack[CAMP_BOT_SETTINGS_KEY].targetType = targetType

    nextPlugins.attacknomads.state = enabled && targetType === CAMP_BOT_TARGET_TYPES.nomad
    nextPlugins.attacksams.state = enabled && targetType === CAMP_BOT_TARGET_TYPES.samurai

    return nextPlugins
}

export const buildCampEventFunctionToggleState = (userPlugins, targetType, nextState) => {
    const nextPlugins = cloneValue(userPlugins ?? {})
    const normalizedTargetType = targetType === CAMP_BOT_TARGET_TYPES.samurai ?
        CAMP_BOT_TARGET_TYPES.samurai :
        CAMP_BOT_TARGET_TYPES.nomad
    const enabled = Boolean(nextState)

    nextPlugins.attack ??= {}
    nextPlugins.attacknomads ??= {}
    nextPlugins.attacksams ??= {}

    const savedSettings = nextPlugins.attack[CAMP_BOT_SETTINGS_KEY]
    nextPlugins.attack[CAMP_BOT_SETTINGS_KEY] =
        savedSettings && typeof savedSettings === 'object' && !Array.isArray(savedSettings) ?
            savedSettings :
            {}

    if (enabled) {
        nextPlugins.attack.state = true
        nextPlugins.attack[CAMP_BOT_SETTINGS_KEY].enabled = true
        nextPlugins.attack[CAMP_BOT_SETTINGS_KEY].targetType = normalizedTargetType
        nextPlugins.attacknomads.state = normalizedTargetType === CAMP_BOT_TARGET_TYPES.nomad
        nextPlugins.attacksams.state = normalizedTargetType === CAMP_BOT_TARGET_TYPES.samurai
        return nextPlugins
    }

    if (normalizedTargetType === CAMP_BOT_TARGET_TYPES.nomad)
        nextPlugins.attacknomads.state = false
    else
        nextPlugins.attacksams.state = false

    if (!nextPlugins.attacknomads.state && !nextPlugins.attacksams.state) {
        nextPlugins.attack.state = false
        nextPlugins.attack[CAMP_BOT_SETTINGS_KEY].enabled = false
    }

    return nextPlugins
}

export const buildSpeedFunctionToggleState = (userPlugins, nextState) => {
    const nextPlugins = cloneValue(userPlugins ?? {})
    nextPlugins.autospeed ??= {}
    nextPlugins.autospeed.state = Boolean(nextState)
    nextPlugins.autospeed.enabled = Boolean(nextState)
    return nextPlugins
}

function FunctionToggleItem({ item, checked, onChange, onSelect }) {
    const helperText = `Enable or disable ${item.label}. Click the row to open settings.`

    return (
        <Box
            onClick={onSelect}
            sx={{
                minHeight: 50.5,
                px: 1.15,
                py: 0.95,
                mx: -1.15,
                borderRadius: '12px',
                cursor: onSelect ? 'pointer' : 'default',
                transition: 'background-color 0.18s ease, transform 0.18s ease',
                '&:hover': onSelect ? {
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    transform: 'translateX(2px)'
                } : undefined
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1.1}>
                <Switch
                    checked={checked}
                    inputProps={{ 'aria-label': item.label }}
                    onChange={event => onChange(event.target.checked)}
                    onClick={event => event.stopPropagation()}
                    sx={{
                        width: 48,
                        height: 29,
                        p: 0,
                        mr: 0.1,
                        '& .MuiSwitch-switchBase': {
                            p: '2.5px',
                            '&.Mui-checked': {
                                transform: 'translateX(18px)',
                                color: '#ffffff'
                            },
                            '&.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#2f7cff',
                                opacity: 1
                            }
                        },
                        '& .MuiSwitch-thumb': {
                            width: 24,
                            height: 24,
                            boxShadow: 'none'
                        },
                        '& .MuiSwitch-track': {
                            opacity: 1,
                            borderRadius: '999px',
                            backgroundColor: 'rgba(111, 123, 143, 0.9)'
                        }
                    }}
                />
                <Typography
                    sx={{
                        color: '#f5f7ff',
                        fontSize: '1rem',
                        lineHeight: 1.15,
                        fontWeight: 700,
                        letterSpacing: '-0.01em'
                    }}
                >
                    {item.label}
                </Typography>
            </Stack>

            <Stack direction="row" spacing={0.55} alignItems="center" sx={{ mt: 0.65, pl: 0.1 }}>
                <InfoOutlinedIcon sx={{ fontSize: 12.5, color: '#96a5c0' }} />
                <Typography
                    sx={{
                        color: '#96a5c0',
                        fontSize: '0.865rem',
                        lineHeight: 1.25,
                        letterSpacing: '-0.01em'
                    }}
                >
                    {helperText}
                </Typography>
            </Stack>
        </Box>
    )
}

export default function FunctionSelectionPanel({ userPlugins, onChange, onModuleSelect, bthMode = false }) {
    const visibleColumns = React.useMemo(
        () => buildAlphabeticalFunctionColumns(getVisibleFunctionSelectionItems(FUNCTION_SELECTION_ITEMS, bthMode)),
        [bthMode]
    )
    const updateFunctionState = (item, nextState) => {
        if (item.id === 'nomads-bot' || item.id === 'samurai-bot') {
            const targetType = item.id === 'samurai-bot' ? CAMP_BOT_TARGET_TYPES.samurai : CAMP_BOT_TARGET_TYPES.nomad
            onChange(buildCampEventFunctionToggleState(userPlugins, targetType, nextState))
            return
        }

        if (item.id === 'speed-settings') {
            onChange(buildSpeedFunctionToggleState(userPlugins, nextState))
            return
        }

        const nextPlugins = cloneValue(userPlugins ?? {})

        if (item.id === 'bth-influence-hunter') {
            nextPlugins.bth_influence_hunter ??= {}
            nextPlugins.bth_influence_hunter.state = Boolean(nextState)
            nextPlugins.bth_influence_hunter.enabled = Boolean(nextState)
            onChange(nextPlugins)
            return
        }

        if (item.id === 'storm-bot') {
            nextPlugins.attackforts ??= {}
            nextPlugins.attackforts.state = Boolean(nextState)

            const cargoPlugin = nextPlugins[STORM_CARGO_PLUGIN_KEY]
            const cargoSettings = cargoPlugin?.[STORM_CARGO_SETTINGS_KEY]
            const cargoAutomationEnabled = Boolean(
                cargoPlugin?.enabled ||
                cargoSettings?.shipBuildingEnabled ||
                cargoSettings?.autoUpgradeStorehouse
            )

            if (cargoPlugin || !nextState) {
                nextPlugins[STORM_CARGO_PLUGIN_KEY] ??= {}
                nextPlugins[STORM_CARGO_PLUGIN_KEY].state = Boolean(nextState) && cargoAutomationEnabled
            }

            onChange(nextPlugins)
            return
        }

        if (item.id === 'tower-bot' || item.id === 'bth-tower-bot') {
            const runtimeStates = Boolean(nextState) ?
                resolveGroupedModuleRuntimeStates(nextPlugins, item.pluginKeys, TOWER_BOT_SETTINGS_KEY) :
                Object.fromEntries(item.pluginKeys.map(pluginKey => [pluginKey, false]))

            item.pluginKeys.forEach(pluginKey => {
                nextPlugins[pluginKey] ??= {}
                nextPlugins[pluginKey].state = Boolean(runtimeStates[pluginKey])
            })

            onChange(nextPlugins)
            return
        }

        if (item.id === 'fortress-bot') {
            const runtimeStates = Boolean(nextState) ?
                resolveGroupedModuleRuntimeStates(nextPlugins, item.pluginKeys, FORTRESS_BOT_SETTINGS_KEY) :
                Object.fromEntries(item.pluginKeys.map(pluginKey => [pluginKey, false]))

            item.pluginKeys.forEach(pluginKey => {
                nextPlugins[pluginKey] ??= {}
                nextPlugins[pluginKey].state = Boolean(runtimeStates[pluginKey])
            })

            onChange(nextPlugins)
            return
        }

        for (const pluginKey of item.pluginKeys) {
            nextPlugins[pluginKey] ??= {}
            nextPlugins[pluginKey].state = Boolean(nextState)
        }

        onChange(nextPlugins)
    }

    const handleFunctionToggle = (item, nextState) => {
        updateFunctionState(item, nextState)

        if (!nextState)
            return

        const moduleTarget = resolveFunctionTarget(item)

        if (moduleTarget)
            onModuleSelect?.(moduleTarget)
    }

    const handleFunctionSelect = item => {
        const moduleTarget = resolveFunctionTarget(item)
        if (moduleTarget)
            onModuleSelect?.(moduleTarget)
    }

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                columnGap: { xs: 0, md: '6.2rem' },
                rowGap: { xs: 2.2, md: 0.2 },
                pt: 0.2
            }}
        >
            {visibleColumns.map((columnItems, columnIndex) => (
                <Stack key={`function-selection-column-${columnIndex}`} spacing={2.08}>
                    {columnItems.map(item => (
                        <FunctionToggleItem
                            key={item.id}
                            item={item}
                            checked={isFunctionEnabled(userPlugins, item, item.pluginKeys)}
                            onChange={nextState => handleFunctionToggle(item, nextState)}
                            onSelect={() => handleFunctionSelect(item)}
                        />
                    ))}
                </Stack>
            ))}
        </Box>
    )
}
